import { useRef, useState } from "react";
import { FORM_ENDPOINT } from "@/config/forms";
import { submitForm } from "@/services/formService";
import { sanitize, validateLead } from "@/utils/validation";
import { trackFormStart, trackLead } from "@/utils/analytics";

interface LeadFormProps {
  formId: string;
  /** Optional endpoint override. Defaults to the global configurable endpoint. */
  endpoint?: string;
  title: string;
  subtitle?: string;
  submitLabel?: string;
  serviceOptions?: string[];
  variant?: "card" | "flat";
  className?: string;
}

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200";

export default function LeadForm({
  formId,
  endpoint = FORM_ENDPOINT,
  title,
  subtitle,
  submitLabel = "SUBMIT",
  serviceOptions = [],
  variant = "card",
  className = "",
}: LeadFormProps) {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");
  const startedRef = useRef(false);

  const handleFormStart = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackFormStart(formId);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    // Read straight from the DOM so browser autofill values are captured too.
    const formData = new FormData(form);

    // Client-side validation for instant feedback (server re-validates).
    const validationError = validateLead({
      name: sanitize(formData.get("name"), 120),
      email: sanitize(formData.get("email"), 160),
      phone: sanitize(formData.get("phone"), 40),
      service: sanitize(formData.get("service"), 80),
      requireService: serviceOptions.length > 0,
    });

    if (validationError) {
      setStatus("error");
      setFeedback(validationError.message);
      return;
    }

    setStatus("submitting");
    setFeedback("");

    const result = await submitForm(form, { endpoint, honeypotField: "company_alt" });

    if (result.status === "success") {
      setStatus("success");
      setFeedback(result.message);
      trackLead(formId);
      form.reset();
    } else {
      setStatus("error");
      setFeedback(result.message);
    }
  };

  const wrapper =
    variant === "card" ? "rounded-lg border border-background-200 bg-background-50 p-6 md:p-7" : "";

  return (
    <div className={`${wrapper} ${className}`}>
      <h3 className="font-heading text-xl font-bold text-foreground-950">{title}</h3>
      {subtitle && <p className="mt-2 text-sm leading-relaxed text-foreground-600">{subtitle}</p>}

      <form
        id={formId}
        action={endpoint}
        method="post"
        noValidate
        onSubmit={handleSubmit}
        onFocus={handleFormStart}
        className="mt-5 space-y-3"
      >
        <input type="text" name="name" required maxLength={120} placeholder="Enter your name" className={inputClass} />
        <input type="email" name="email" required maxLength={160} placeholder="Enter your email" className={inputClass} />
        <input type="tel" name="phone" required maxLength={40} placeholder="Enter your phone number" className={inputClass} />

        {serviceOptions.length > 0 && (
          <select name="service" required defaultValue="" className={inputClass}>
            <option value="" disabled>
              Select the service you need
            </option>
            {serviceOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}

        <textarea
          name="message"
          rows={4}
          maxLength={500}
          placeholder="Add your message (shifting from, shifting to, preferred date)"
          className={`${inputClass} resize-none`}
        />

        {/* Anti-spam field */}
        <input
          type="text"
          name="company_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="field-aux-hidden"
        />

        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <i className="ri-send-plane-fill" aria-hidden="true" />
          {status === "submitting" ? "SENDING..." : submitLabel}
        </button>

        {feedback && (
          <p
            role="status"
            className={`rounded-md px-3 py-2 text-xs ${
              status === "success" ? "bg-accent-100 text-accent-900" : "bg-primary-100 text-primary-800"
            }`}
          >
            {feedback}
          </p>
        )}

        <p className="text-center text-xs text-foreground-500">100% Privacy Guaranteed.</p>
      </form>
    </div>
  );
}